import { useState, useEffect } from 'react';
import Breadcrumb from '../components/sections/Breadcrumb';
import { categoriesApi } from '../services/api';
import type { Category, Subcategory } from '../types';
import { getDisplayYear } from '../utils/yearMapper';

const Categories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Record<number, Subcategory[]>>({});
  const [subcategoryCounts, setSubcategoryCounts] = useState<Record<number, number>>({});
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch categories and subcategory counts on mount
  useEffect(() => {
    let isMounted = true;

    const fetchCategoriesAndCounts = async () => {
      try {
        setLoading(true);
        const categoriesData = await categoriesApi.getAll();
        
        if (!isMounted) return;
        
        setCategories(categoriesData);
        
        // Fetch subcategory counts for all categories
        const counts: Record<number, number> = {};
        await Promise.all(
          categoriesData.map(async (category) => {
            try {
              const subs = await categoriesApi.getSubcategories(category.id);
              counts[category.id] = subs.length;
            } catch (error) {
              console.warn(`Failed to fetch subcategories for category ${category.id}:`, error);
              counts[category.id] = 0;
            }
          })
        );
        
        if (isMounted) {
          setSubcategoryCounts(counts);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCategoriesAndCounts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch subcategories only when a category is opened
  const toggleCategory = async (categoryId: number) => {
    // If closing, just close it
    if (openCategory === categoryId) {
      setOpenCategory(null);
      return;
    }

    // If opening, set as open
    setOpenCategory(categoryId);

    // If subcategories already loaded, don't fetch again
    if (subcategories[categoryId]) {
      return;
    }

    // Fetch subcategories for this category
    try {
      const subs = await categoriesApi.getSubcategories(categoryId);
      // Sort subcategories by ID in ascending order (21, 22, 23, 24, etc.)
      const sortedSubs = subs.sort((a, b) => a.id - b.id);
      setSubcategories(prev => ({ ...prev, [categoryId]: sortedSubs }));
    } catch (error) {
      console.warn(`Failed to fetch subcategories for category ${categoryId}:`, error);
      setSubcategories(prev => ({ ...prev, [categoryId]: [] }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Breadcrumb title="Award Categories" subtitle="CATEGORIES" />

      {/* Hero Section */}
      <section 
        className="py-20 bg-white"
        style={{
          padding: '5rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-4xl mx-auto text-left md:text-center"
            style={{
              maxWidth: '56rem',
              margin: '0 auto'
            }}
          >
            <span 
              className="font-semibold text-sm uppercase tracking-wider mb-4 block"
              style={{
                color: '#ffba17',
                fontWeight: '600',
                fontSize: '0.875rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                marginBottom: '1rem',
                display: 'block'
              }}
            >
              Award Categories
            </span>
            <h2 
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              style={{
                fontSize: 'clamp(2.25rem, 5vw, 3rem)',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}
            >
              Celebrating Excellence Across<br />All Digital Sectors
            </h2>
            <p 
              className="text-xl text-gray-600 leading-relaxed"
              style={{
                fontSize: '1.25rem',
                color: '#4B5563',
                lineHeight: '1.75'
              }}
            >
              The Tanzania Digital Awards recognizes outstanding achievements across multiple categories,
              each representing a crucial aspect of Tanzania's digital transformation journey.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section 
        className="py-4 sm:py-6 md:py-8 bg-white border-b border-gray-200 sticky top-[60px] z-20"
        style={{
          padding: '1rem 0',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          position: 'sticky',
          top: '60px',
          zIndex: 20
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          {/* Mobile Category Filter */}
          <div className="block sm:hidden mb-4">
            <select
              className="w-full py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg appearance-none text-gray-800 font-medium"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#F9FAFB',
                borderColor: '#E5E7EB',
                borderRadius: '0.5rem',
                appearance: 'none',
                color: '#1F2937',
                fontWeight: '500'
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "all") {
                  setOpenCategory(null);
                } else {
                  setOpenCategory(parseInt(value));
                  setTimeout(() => {
                    const element = document.getElementById(`category-${value}`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }
              }}
              value={openCategory === null ? "all" : openCategory.toString()}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={`select-${category.id}`} value={category.id.toString()}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Desktop Category Filter */}
          <div className="hidden sm:flex flex-wrap justify-center gap-2 mb-4">
            <button 
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium text-white"
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: '#ffba17',
                color: 'white'
              }}
              onClick={() => setOpenCategory(null)}
            >
              All Categories
            </button>
            <button 
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: '#F3F4F6',
                color: '#374151'
              }}
              onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
            >
              <i className="fa-solid fa-arrow-up mr-1"></i> Back to Top
            </button>
          </div>
          <p className="text-center text-gray-600 text-xs sm:text-sm">
            <span className="font-semibold">{categories.length}</span> categories with <span className="font-semibold">{Object.values(subcategoryCounts).reduce((sum, count) => sum + count, 0)}</span> subcategories
          </p>
        </div>
      </section>

      {/* Categories Grid */}
      <section 
        className="py-8 sm:py-10 md:py-12 bg-gray-50"
        style={{
          padding: '2rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-3 sm:px-4"
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 0.75rem'
          }}
        >
          <div 
            className="max-w-7xl mx-auto"
            style={{
              maxWidth: '80rem',
              margin: '0 auto'
            }}
          >
            {loading ? (
              <div 
                className="text-center py-8 sm:py-12"
                style={{
                  textAlign: 'center',
                  padding: '2rem 0'
                }}
              >
                <div 
                  className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 mx-auto"
                  style={{
                    animation: 'spin 1s linear infinite',
                    height: '3rem',
                    width: '3rem',
                    borderBottomWidth: '2px',
                    borderColor: '#ffba17',
                    borderRadius: '50%',
                    margin: '0 auto'
                  }}
                ></div>
                <p 
                  className="text-gray-600 mt-4 text-sm sm:text-base"
                  style={{
                    color: '#4B5563',
                    marginTop: '1rem',
                    fontSize: '0.875rem'
                  }}
                >
                  Loading categories...
                </p>
              </div>
            ) : (
              <div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem'
                }}
              >
                {categories.map((category, index) => (
                  <div
                    id={`category-${category.id}`}
                    key={category.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(openCategory === category.id ? {
                        boxShadow: '0 20px 25px -5px rgba(255, 186, 23, 0.25), 0 10px 10px -5px rgba(255, 186, 23, 0.1)',
                        transform: 'translateY(-2px)'
                      } : {})
                    }}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {/* Category Header */}
                    <div 
                      className="p-4 sm:p-5 md:p-6 relative overflow-hidden"
                      style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <div 
                        className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white opacity-5 rounded-full transform translate-x-8 -translate-y-8"
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          width: '6rem',
                          height: '6rem',
                          backgroundColor: 'white',
                          opacity: '0.05',
                          borderRadius: '50%',
                          transform: 'translate(2rem, -2rem)'
                        }}
                      ></div>
                      <div 
                        className="relative z-10 flex items-center"
                        style={{
                          position: 'relative',
                          zIndex: '10',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div 
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0"
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: '#ffba17',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem',
                            flexShrink: 0
                          }}
                        >
                          <span 
                            className="text-white text-sm sm:text-lg font-bold"
                            style={{
                              color: 'white',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <div>
                          <h3 
                            className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight"
                            style={{
                              fontSize: '1rem',
                              fontWeight: '700',
                              color: 'white',
                              lineHeight: '1.3'
                            }}
                          >
                            {category.name}
                          </h3>
                          <div 
                            className="text-xs text-gray-300 font-medium mt-1"
                            style={{
                              fontSize: '0.75rem',
                              color: '#D1D5DB',
                              fontWeight: '500',
                              marginTop: '0.25rem'
                            }}
                          >
                            {subcategoryCounts[category.id] || 0} Subcategories
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category Content */}
                    <div 
                      className="p-4 sm:p-5 flex-grow flex flex-col"
                      style={{
                        padding: '1rem',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <p 
                        className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 flex-grow"
                        style={{
                          color: '#4B5563',
                          fontSize: '0.75rem',
                          lineHeight: '1.75',
                          marginBottom: '0.75rem',
                          flexGrow: 1
                        }}
                      >
                        {category.description.length > 120
                          ? `${category.description.substring(0, 120)}...` 
                          : category.description}
                      </p>

                      {/* Subcategories */}
                      {subcategories[category.id] && subcategories[category.id].length > 0 && (
                        <div 
                          className={`transition-all duration-500 ease-in-out ${openCategory === category.id ? 'max-h-[500px] opacity-100 scale-100' : 'max-h-0 opacity-0 scale-95 overflow-hidden'}`}
                          style={{
                            transition: 'all 0.5s ease-in-out',
                            maxHeight: openCategory === category.id ? '500px' : '0',
                            opacity: openCategory === category.id ? '1' : '0',
                            transform: openCategory === category.id ? 'scale(1)' : 'scale(0.95)',
                            overflow: openCategory === category.id ? 'auto' : 'hidden',
                            transformOrigin: 'top center'
                          }}
                        >
                          <div 
                            className="border-t border-gray-200 pt-4 mt-3 bg-gray-50 rounded-lg p-3"
                            style={{
                              borderTop: '1px solid #E5E7EB',
                              paddingTop: '1rem',
                              marginTop: '0.75rem',
                              backgroundColor: '#F9FAFB',
                              borderRadius: '0.5rem',
                              padding: '0.75rem'
                            }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <h4 
                                className="text-xs font-semibold text-gray-900 uppercase tracking-wide"
                                style={{
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  color: '#111827',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em'
                                }}
                              >
                                Subcategories ({subcategories[category.id].length})
                              </h4>
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                {subcategories[category.id][0]?.year ? getDisplayYear(subcategories[category.id][0].year) : '2024'}
                              </span>
                            </div>
                            
                            <ul 
                              className="space-y-2 max-h-[300px] overflow-y-auto pr-2"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                paddingRight: '0.5rem'
                              }}
                            >
                              {subcategories[category.id].map((sub) => (
                                <li 
                                  key={sub.id} 
                                  className="flex items-start bg-white p-2 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    backgroundColor: 'white',
                                    padding: '0.5rem',
                                    borderRadius: '0.375rem',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                    transition: 'box-shadow 0.2s ease'
                                  }}
                                >
                                  <div 
                                    className="w-5 h-5 bg-gold rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                                    style={{
                                      width: '1.25rem',
                                      height: '1.25rem',
                                      backgroundColor: '#ffba17',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: '0.5rem',
                                      flexShrink: 0
                                    }}
                                  >
                                    <i 
                                      className="fa-solid fa-check text-white text-xs"
                                      style={{
                                        color: 'white',
                                        fontSize: '0.625rem'
                                      }}
                                    ></i>
                                  </div>
                                  <div>
                                    <span 
                                      className="text-xs font-medium text-gray-900"
                                      style={{
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        color: '#111827'
                                      }}
                                    >
                                      {sub.name}
                                    </span>
                                    <p 
                                      className="text-[10px] text-gray-500 mt-0.5"
                                      style={{
                                        fontSize: '10px',
                                        color: '#6B7280',
                                        marginTop: '0.125rem'
                                      }}
                                    >
                                      {sub.description}
                                    </p>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* View More Button */}
                      <button 
                        className="mt-3 sm:mt-4 flex items-center justify-center w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-md transition-all duration-300"
                        style={{
                          marginTop: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '0.375rem',
                          fontWeight: '600',
                          fontSize: '0.75rem',
                          transition: 'all 0.3s ease',
                          backgroundColor: openCategory === category.id ? '#0f1b3c' : '#FFF7ED',
                          color: openCategory === category.id ? '#FFFFFF' : '#c2410c',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        {openCategory === category.id ? 'Hide Subcategories' : 'View All Subcategories'}
                        <i 
                          className={`fa-solid fa-chevron-${openCategory === category.id ? 'up' : 'down'} ml-1.5 sm:ml-2`}
                          style={{
                            marginLeft: '0.375rem',
                            fontSize: '0.675rem'
                          }}
                        ></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile Category View */}
      <section className="py-8 bg-white lg:hidden">
        <div className="container mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Quick Category Navigation</h3>
          
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {categories.map((category) => (
              <button
                key={`mobile-${category.id}`}
                className="px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  backgroundColor: openCategory === category.id ? '#0f1b3c' : '#F3F4F6',
                  color: openCategory === category.id ? 'white' : '#374151',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  setOpenCategory(openCategory === category.id ? null : category.id);
                  // Scroll to the category if opening
                  if (openCategory !== category.id) {
                    setTimeout(() => {
                      document.getElementById(`mobile-subcategories-${category.id}`)?.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }
                }}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Mobile Subcategories View */}
          {openCategory !== null && (
            <div 
              id={`mobile-subcategories-${openCategory}`}
              className="bg-white rounded-xl shadow-md p-4 mb-8"
              style={{
                backgroundColor: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                padding: '1rem',
                marginBottom: '2rem'
              }}
            >
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
                <h4 className="text-lg font-bold" style={{ color: '#0f1b3c' }}>
                  {categories.find(c => c.id === openCategory)?.name}
                </h4>
                <span className="text-xs text-white px-2 py-1 rounded-full" style={{ backgroundColor: '#ffba17' }}>
                  {subcategories[openCategory]?.length || 0} Subcategories
                </span>
              </div>
              
              <ul className="space-y-3">
                {subcategories[openCategory]?.map((sub) => (
                  <li key={`mobile-sub-${sub.id}`} className="flex items-center p-2 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: '#ffba17' }}>
                      <i className="fa-solid fa-check text-white text-xs"></i>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900">{sub.name}</h5>
                      <p className="text-xs text-gray-500 mt-1">{sub.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-12 sm:py-16 md:py-20 relative overflow-hidden"
        style={{
          padding: '3rem 0',
          background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative elements */}
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-10"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.1
          }}
        >
          <div className="absolute w-40 sm:w-64 h-40 sm:h-64 rounded-full bg-white -top-20 -left-20"></div>
          <div className="absolute w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-white -bottom-40 -right-20"></div>
        </div>

        <div 
          className="container mx-auto px-4 text-center relative z-10"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10
          }}
        >
          <div 
            className="max-w-4xl mx-auto"
            style={{
              maxWidth: '56rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="inline-block text-white text-xs font-semibold px-2 sm:px-3 py-1 rounded-full mb-4 sm:mb-6"
              style={{
                display: 'inline-block',
                backgroundColor: '#ffba17',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: '0.25rem 0.5rem',
                borderRadius: '9999px',
                marginBottom: '1rem'
              }}
            >
              {categories.length} Categories • 92 Subcategories
            </div>

            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6"
              style={{
                fontSize: 'clamp(1.5rem, 5vw, 3rem)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '1rem',
                lineHeight: '1.2'
              }}
            >
              Ready to Showcase Your Digital Excellence?
            </h2>
            <p 
              className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto"
              style={{
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                color: '#E5E7EB',
                marginBottom: '1.5rem',
                lineHeight: '1.75',
                maxWidth: '48rem',
                margin: '0 auto 1.5rem'
              }}
            >
              Think you or someone you know deserves recognition? Submit your nomination today
              and be part of Tanzania's digital excellence story.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a 
                href="/nominate/"
                className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 text-white rounded-lg font-bold text-base sm:text-lg transition-all duration-300 shadow-lg w-full sm:w-auto justify-center"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#ffba17',
                  color: 'white',
                  borderRadius: '3rem',
                  fontWeight: '700',
                  fontSize: '1rem',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 15px -3px rgba(255, 186, 23, 0.3), 0 4px 6px -2px rgba(255, 186, 23, 0.2)',
                  width: 'auto'
                }}
              >
                <i 
                  className="fa-solid fa-trophy mr-2 sm:mr-3"
                  style={{
                    marginRight: '0.5rem'
                  }}
                ></i>
                Nominate Now
              </a>
              
              <a 
                href="/about"
                className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-base sm:text-lg transition-all duration-300 w-full sm:w-auto justify-center"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#0f1b3c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'white';
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'transparent',
                  borderWidth: '2px',
                  borderColor: 'white',
                  color: 'white',
                  borderRadius: '3rem',
                  fontWeight: '700',
                  fontSize: '1rem',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  width: 'auto'
                }}
              >
                <i 
                  className="fa-solid fa-circle-info mr-2 sm:mr-3"
                  style={{
                    marginRight: '0.5rem'
                  }}
                ></i>
                Learn More
              </a>
            </div>
            
            <div className="mt-16 sm:mt-18 text-xs sm:text-sm text-gray-300">
              <p>Nominations close on December 15, 2025</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Categories;